import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { useEffect, useState, createContext, useContext } from "react";
import axios from "axios";
import "@/App.css";

// Pages
import Home from "@/pages/Home";
import LiveTV from "@/pages/LiveTV";
import Watch from "@/pages/Watch";
import Countries from "@/pages/Countries";
import Categories from "@/pages/Categories";
import CategoryDetail from "@/pages/CategoryDetail";
import CustomPage from "@/pages/CustomPage";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminChannels from "@/pages/admin/Channels";
import AdminCategories from "@/pages/admin/Categories";
import AdminCountries from "@/pages/admin/Countries";
import AdminAds from "@/pages/admin/Ads";
import AdminSettings from "@/pages/admin/Settings";
import AdminMembership from "@/pages/admin/Membership";
import AdminUsers from "@/pages/admin/Users";
import AdminChat from "@/pages/admin/Chat";
import AdminMessages from "@/pages/admin/Messages";
import AdminPages from "@/pages/admin/Pages";
import AdminLanguages from "@/pages/admin/Languages";
import PublicLanguages from "@/pages/PublicLanguages";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminAuditLog from "@/pages/admin/AuditLog";
import AdminHomepage from "@/pages/admin/Homepage";
import AdminChannelSubmissions from "@/pages/admin/ChannelSubmissions";
import AdminForms from "@/pages/admin/Forms";
import AdminFormEditor from "@/pages/admin/FormEditor";
import AdminFormSubmissions from "@/pages/admin/FormSubmissions";
import AdminSliders from "@/pages/admin/Sliders";
import AdminBanners from "@/pages/admin/Banners";
import ModeratorDashboard from "@/pages/moderator/Dashboard";
import Membership from "@/pages/Membership";
import Account from "@/pages/Account";
import FAQ from "@/pages/FAQ";
import Support from "@/pages/Support";
import Report from "@/pages/Report";
import SubmitChannel from "@/pages/SubmitChannel";
import DynamicForm from "@/pages/DynamicForm";

// Components
import Navbar from "@/components/Navbar";
import Sidebar from "@/components/Sidebar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import AdminLayout from "@/components/AdminLayout";
import ScrollToTop from "@/components/ScrollToTop";
import MaintenancePage from "@/components/MaintenancePage";

function FormPreviewRoute() {
  const { previewId } = useParams();
  return <DynamicForm previewFormId={previewId} />;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function getApiBase() {
  const currentHost = window.location.hostname;
  if (BACKEND_URL && BACKEND_URL.includes(currentHost)) {
    return `${BACKEND_URL}/api`;
  }
  return '/api';
}

export const API = getApiBase();

// Settings Context
export const SettingsContext = createContext(null);

// Protected Route Component for admin nested routes
function ProtectedAdminRoute() {
  const [auth, setAuth] = useState({ loading: true, authenticated: false });

  useEffect(() => {
    axios.get(`${API}/auth/check`, { withCredentials: true })
      .then((res) => {
        setAuth({ loading: false, authenticated: res.data.authenticated });
      })
      .catch(() => {
        setAuth({ loading: false, authenticated: false });
      });
  }, []);

  if (auth.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#050814]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!auth.authenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <AdminLayout />;
}

function AppContent() {
  const location = useLocation();
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('site_settings');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });

  useEffect(() => {
    if (settings?.site_name) document.title = settings.site_name;
    if (settings?.favicon_url) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = settings.favicon_url;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    axios.get(`${API}/settings`)
      .then((res) => {
        setSettings(res.data);
        localStorage.setItem('site_settings', JSON.stringify(res.data));
        if (res.data.site_name) document.title = res.data.site_name;
        if (res.data.favicon_url) {
          const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
          link.type = 'image/x-icon';
          link.rel = 'shortcut icon';
          link.href = res.data.favicon_url;
          document.getElementsByTagName('head')[0].appendChild(link);
        }
      })
      .catch(() => {});
  }, []);

  // Heartbeat for live user tracking
  useEffect(() => {
    const sendHeartbeat = () => {
      axios.post(`${API}/heartbeat`, {}, { withCredentials: true }).catch(() => {});
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(interval);
  }, []);

  // Hide Google AdSense for premium users
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user_data');
      if (!userData) return;
      const user = JSON.parse(userData);
      if (user.has_membership && user.membership_expires && new Date(user.membership_expires) > new Date()) {
        const adsScript = document.querySelector('script[src*="adsbygoogle"]');
        if (adsScript) adsScript.remove();
        const style = document.createElement('style');
        style.id = 'hide-ads-premium';
        style.textContent = 'ins.adsbygoogle, [id^="google_ads"], [id^="aswift_"], iframe[src*="googlesyndication"], iframe[src*="doubleclick"] { display: none !important; height: 0 !important; }';
        if (!document.getElementById('hide-ads-premium')) {
          document.head.appendChild(style);
        }
      } else {
        const hideStyle = document.getElementById('hide-ads-premium');
        if (hideStyle) hideStyle.remove();
      }
    } catch {}
  });

  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/moderator');

  // Maintenance gate: block non-admin visitors when maintenance mode is active.
  const [adminLevel, setAdminLevel] = useState(null);
  useEffect(() => {
    if (settings?.maintenance_mode && !isAdminRoute) {
      axios.get(`${API}/auth/me`, { withCredentials: true })
        .then((r) => setAdminLevel(r.data?.authenticated ? (r.data.level || 0) : 0))
        .catch(() => setAdminLevel(0));
    }
  }, [settings?.maintenance_mode, isAdminRoute]);

  if (settings?.maintenance_mode && !isAdminRoute && adminLevel !== null && adminLevel < 80) {
    return (
      <SettingsContext.Provider value={{ settings, setSettings }}>
        <MaintenancePage settings={settings} />
      </SettingsContext.Provider>
    );
  }

  return (
    <SettingsContext.Provider value={{ settings, setSettings }}>
      <ScrollToTop />
      <div className="min-h-screen bg-[#050814] text-white flex flex-col" data-testid="site-root">
        {isAdminRoute ? (
          <div className="flex-1">
            <Routes>
              <Route path="/admin/login" element={<AdminLogin />} />
              {/* Nested admin routes - all share AdminLayout with sidebar */}
              <Route path="/admin" element={<ProtectedAdminRoute />}>
                <Route index element={<AdminDashboard />} />
                <Route path="homepage" element={<AdminHomepage />} />
                <Route path="sliders" element={<AdminSliders />} />
                <Route path="banners" element={<AdminBanners />} />
                <Route path="channels" element={<AdminChannels />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="countries" element={<AdminCountries />} />
                <Route path="channel-submissions" element={<AdminChannelSubmissions />} />
                <Route path="forms" element={<AdminForms />} />
                <Route path="forms/:formId/edit" element={<AdminFormEditor />} />
                <Route path="forms/:formId/submissions" element={<AdminFormSubmissions />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="audit-log" element={<AdminAuditLog />} />
                <Route path="ads" element={<AdminAds />} />
                <Route path="membership" element={<AdminMembership />} />
                <Route path="chat" element={<AdminChat />} />
                <Route path="chat/:conversationId" element={<AdminChat />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="pages" element={<AdminPages />} />
                <Route path="languages" element={<AdminLanguages />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="/moderator" element={<ModeratorDashboard />} />
            </Routes>
          </div>
        ) : (
          <>
            <div className="flex-1 lg:grid lg:grid-cols-[var(--sidebar-w,220px)_minmax(0,1fr)]" data-testid="dashboard-shell">
              <Sidebar />
              <div className="min-w-0 flex flex-col">
                <Navbar />
                <main id="main-content" tabIndex={-1} className="flex-1 pb-20 lg:pb-0 outline-none">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/live" element={<LiveTV />} />
                    <Route path="/watch/:id" element={<Watch />} />
                    <Route path="/countries" element={<Countries />} />
                    <Route path="/languages" element={<PublicLanguages />} />
                    <Route path="/languages/:slug" element={<PublicLanguages />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/categories/:slug" element={<CategoryDetail />} />
                    <Route path="/about" element={<CustomPage slug="about" />} />
                    <Route path="/terms" element={<CustomPage slug="terms" />} />
                    <Route path="/privacy" element={<CustomPage slug="privacy" />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/contact" element={<CustomPage slug="contact" />} />
                    <Route path="/report" element={<Report />} />
                    <Route path="/submit-channel" element={<SubmitChannel />} />
                    <Route path="/forms/:key" element={<DynamicForm />} />
                    <Route path="/forms-preview/:previewId" element={<FormPreviewRoute />} />
                    <Route path="/subscription" element={<Membership />} />
                    <Route path="/page/:slug" element={<CustomPage />} />
                    <Route path="/membership" element={<Membership />} />
                    <Route path="/account" element={<Account />} />
                  </Routes>
                </main>
              </div>
            </div>
            <Footer />
            <ChatWidget />
          </>
        )}
      </div>
    </SettingsContext.Provider>
  );
}

// Ensure httpOnly cookies are sent with all API requests
axios.interceptors.request.use((config) => {
  config.withCredentials = true;
  return config;
});

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
